import { useState, type FormEvent } from 'react';
import { Link } from '../../../components/Link';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../../lib/api';
import { Icon } from '../../../components/icons';
import { Button, Card, Field, cn } from '../../../components/ui';
import { DeferredTurnstileWidget } from '../../../components/TurnstileWidget';
import { useTurnstileGate } from '../../../lib/useAbuseConfig';
import { CopyButton, ToolPageLayout, type ToolPageContent } from './ToolPageLayout';

type Tone = 'professional' | 'friendly' | 'direct' | 'casual' | 'persuasive';

interface AiResult {
  result: string[];
  meta: { provider: string; model: string; inputTokens: number; outputTokens: number; costMicrosUsd: number };
}

const TONES: Array<{ value: Tone; label: string }> = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'direct', label: 'Direct' },
  { value: 'casual', label: 'Casual' },
  { value: 'persuasive', label: 'Persuasive' },
];

/**
 * AI Subject Line Generator — LLM-powered free tool. Hits the same provider+model
 * pipeline as /tools/ai-email-writer; produces 7 variants per call.
 */
export function SubjectLineGeneratorPage() {
  const [context, setContext] = useState('');
  const [tone, setTone] = useState<Tone>('professional');
  const [rateLimited, setRateLimited] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const gate = useTurnstileGate();

  const generate = useMutation<AiResult, ApiError>({
    mutationFn: () =>
      api<AiResult>('/public/tools/ai/subject-lines', {
        method: 'POST',
        body: { context, tone },
        auth: false,
        headers: gate.headers,
      }),
    onSuccess: () => {
      setRateLimited(null);
      setError(null);
    },
    onError: (e) => {
      if (e.status === 429) {
        setRateLimited(e.message);
        setError(null);
      } else {
        setError(e.message);
        setRateLimited(null);
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!context.trim()) return;
    generate.mutate();
  }

  const content: ToolPageContent = {
    slug: 'subject-line-generator',
    title: 'Email subject line generator',
    tagline: 'Get 7 distinct subject lines for any email — instantly.',
    eyebrow: 'Free tool · AI-powered · Rate-limited per IP',
    description:
      'Free AI subject line generator. Get 7 distinct subject-line variants for any email — varied structure, 30–60 character sweet spot, no spam triggers.',
    icon: <Icon.Type />,
    accent: 'verify',
    intro:
      "Describe the email — what it's about, who it's for. We generate 7 subject-line variants that stay in the 30-60 character sweet spot, avoid known spam triggers, and vary structure (question, statement, personalisation, number-led).",
    body: (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
        <Card className="p-6">
          <h2 className="text-sm font-semibold">Email context</h2>
          <form onSubmit={onSubmit} {...gate.armProps} className="mt-5 grid gap-4">
            <Field label="What's the email about?" hint="Plain English — the recipient, the topic, the ask.">
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Following up with Sarah at Acme after our demo last week — she said she'd review pricing with her CFO and get back."
                rows={5}
                maxLength={2000}
                className="block w-full rounded-md border border-border bg-bg p-3 text-[13.5px] text-txt placeholder:text-faint focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                autoFocus
              />
            </Field>

            <Field label="Tone">
              <div className="inline-flex flex-wrap rounded-md border border-border bg-bg p-1 text-[12.5px]">
                {TONES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setTone(t.value)}
                    className={cn(
                      'rounded px-3 py-1.5 transition',
                      tone === t.value ? 'bg-accent text-[#04141d] font-semibold' : 'text-muted hover:bg-hover hover:text-txt',
                    )}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </Field>

            <DeferredTurnstileWidget gate={gate} />
            <Button type="submit" variant="primary" disabled={generate.isPending || !context.trim() || !gate.ready}>
              {generate.isPending ? 'Generating…' : 'Generate 7 subject lines'}
            </Button>
            {error && <p className="text-[13px] text-review">{error}</p>}
            {rateLimited && (
              <p className="text-[13px] text-verify">
                {rateLimited} <Link to="/register" className="text-accent hover:underline">Create a workspace</Link> for unlimited generations.
              </p>
            )}
          </form>
        </Card>

        <Card className="p-6">
          <h2 className="text-sm font-semibold">Subject lines</h2>
          {!generate.data && (
            <div className="mt-4 flex h-64 items-center justify-center text-center text-[13px] text-muted">
              Describe the email on the left, then hit Generate.
            </div>
          )}
          {generate.data && (
            <>
              <ul className="mt-4 divide-y divide-bordersoft">
                {generate.data.result.map((line, i) => (
                  <li key={i} className="flex items-baseline justify-between gap-3 py-3">
                    <div>
                      <div className="font-mono text-[10.5px] uppercase tracking-wider text-faint">#{i + 1} · {line.length} chars</div>
                      <div className="mt-1 text-[14px] font-medium">{line}</div>
                    </div>
                    <CopyButton text={line} />
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-[11.5px] text-faint">
                Generated by {generate.data.meta.provider} / {generate.data.meta.model} ·{' '}
                {generate.data.meta.inputTokens + generate.data.meta.outputTokens} tokens
              </p>
            </>
          )}
        </Card>
      </div>
    ),
    educational: {
      title: 'What makes a subject line work',
      paragraphs: [
        'Subject lines are the single most A/B-tested feature in email, and the public data is remarkably consistent: short beats long (30-60 chars), specific beats generic (numbers, names, dates), and human-sounding beats marketing-sounding. The model is constrained to all three.',
        "Spam triggers are heuristic — single words like 'free' aren't toxic in isolation, but in the wrong subject they tip filters that are already on the edge. The system prompt explicitly forbids the worst offenders ('act now', 'limited time', '100% free', 'click here', 'congratulations') so the output is safe to send from a clean mailbox.",
        'Variety matters across batches. The prompt instructs the model to vary structure — question, statement, personalisation, number-led — so you can A/B-test directions rather than wording. Once you pick a winner, the email body matters more than further subject iteration.',
      ],
    },
    faq: [
      { q: 'Why 7 variants?', a: 'Three is too few to A/B; ten clutters the comparison. Seven is the sweet spot we landed on after testing — enough variety, easy to scan at a glance.' },
      { q: 'Why no emojis by default?', a: 'The prompt allows up to one emoji per subject but doesn\'t insist on them. B2C emails sometimes lift open rates with one emoji; B2B almost never does. Add a "tone: casual" hint if you want emoji-friendly output.' },
      { q: 'Can I score the output?', a: 'Yes — paste any subject into our Subject Line Tester for an A-F grade across length, caps ratio, emoji count, punctuation, urgency, and spam triggers.' },
      { q: 'Does this work for newsletters?', a: 'It works for any cold-or-warm B2B / B2C subject line. For newsletter-specific subjects ("Issue #42: …"), describe that in the context box and the model will follow.' },
    ],
    relatedTools: [
      { slug: 'subject-line-tester', title: 'Subject line tester', tagline: 'Score an existing subject line A–F on 6 dimensions.' },
      { slug: 'ai-email-writer', title: 'AI email writer', tagline: 'Generate the full email, not just the subject.' },
      { slug: 'email-shortener', title: 'Email shortener', tagline: 'Tighten an existing draft to inbox-friendly length.' },
    ],
    ctaTitle: 'A great subject is half the battle',
    ctaBody:
      "The other half is whether the message arrives in the inbox at all. SieveGuard's Inbox Placement add-on sends test emails to seed accounts at every major ESP and reports inbox vs spam vs missing per provider.",
  };

  return <ToolPageLayout content={content} />;
}

// Default export required by React Router framework-mode route modules.
export default SubjectLineGeneratorPage;
